import { useEffect, useState } from "react";
import axios from "axios";
import { Star, CheckCircle2, Loader2, Send } from "lucide-react";
import { COMPANY, curSym } from "@/lib/company";

const pub = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

function Stars({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          data-testid={`${testid}-${n}`}
          className="transition-transform hover:scale-110 focus:outline-none"
          aria-label={`${n} yıldız`}
        >
          <Star className={`h-8 w-8 ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

export default function SurveyPublic() {
  const params = new URLSearchParams(window.location.search);
  const receiptNo = params.get("fis") || "";
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // qid -> {rating} or {choice}
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [coupon, setCoupon] = useState(null);

  useEffect(() => {
    pub.get("/surveys/questions/public").then((r) => setQuestions(r.data.items || [])).catch(() => {});
  }, []);

  const setAns = (qid, patch) => setAnswers((a) => ({ ...a, [qid]: { ...(a[qid] || {}), ...patch } }));

  const submit = async () => {
    if (rating < 1) { setErr("Lütfen genel memnuniyet puanı verin."); return; }
    if (!name.trim()) { setErr("Lütfen ad soyad girin."); return; }
    if (phone.replace(/\D/g, "").length < 10) { setErr("Lütfen geçerli bir telefon numarası girin."); return; }
    setErr(""); setBusy(true);
    try {
      const payloadAnswers = questions
        .map((q) => {
          const a = answers[q.id] || {};
          if (q.type === "rating" && a.rating) return { question_id: q.id, question_text: q.text, rating: a.rating };
          if (q.type === "choice" && a.choice) return { question_id: q.id, question_text: q.text, choice: a.choice };
          return null;
        })
        .filter(Boolean);
      await pub.post("/surveys", {
        rating,
        comment: comment.trim(),
        name: name.trim(),
        phone: phone.trim(),
        receipt_no: receiptNo || null,
        answers: payloadAnswers,
      }).then((r) => setCoupon(r.data?.coupon || null));
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Gönderilemedi, lütfen tekrar deneyin.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white px-4 py-8" data-testid="survey-public-page">
      <div className="mx-auto max-w-lg">
        <div className="mb-6 text-center">
          <h1 className="font-heading text-3xl font-black tracking-tight text-emerald-700">{COMPANY.brand}</h1>
          <p className="mt-1 text-sm text-slate-500">{COMPANY.legal}</p>
        </div>

        {done ? (
          <div className="rounded-3xl border border-emerald-200 bg-white p-8 text-center shadow-lg" data-testid="survey-thankyou">
            <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-500" />
            <h2 className="mt-4 font-heading text-2xl font-black text-slate-900">Teşekkür ederiz!</h2>
            <p className="mt-2 text-slate-600">Ankete katıldığınız için teşekkürler. Görüşleriniz hizmetimizi geliştirmemize yardımcı oluyor.</p>
            {coupon && (
              <div className="mt-5 rounded-2xl border-2 border-dashed border-amber-400 bg-amber-50 p-5" data-testid="survey-coupon">
                <p className="text-sm font-semibold text-amber-700">🎁 Bir sonraki alışverişinize özel indirim!</p>
                <p className="mt-2 font-heading text-2xl font-black tracking-widest text-amber-600" data-testid="survey-coupon-code">{coupon.code}</p>
                <p className="mt-1 text-sm text-slate-600">
                  {coupon.discount_type === "amount" ? `${coupon.discount_value} ${curSym()} indirim` : `%${coupon.discount_value} indirim`}
                  {coupon.min_purchase > 0 ? ` · min. ${coupon.min_purchase} ${curSym()} alışveriş` : ""}
                </p>
                <p className="mt-1 text-xs text-slate-400">Son geçerlilik: {String(coupon.expires_at).slice(0, 10)} · Kodu kasada gösterin.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg sm:p-8">
            <h2 className="font-heading text-xl font-black text-slate-900">Alışverişinizi değerlendirin</h2>
            <p className="mt-1 text-sm text-slate-500">
              {receiptNo ? <>Fiş No: <span className="font-semibold text-slate-700">{receiptNo}</span> · </> : null}
              Birkaç saniyenizi ayırın.
            </p>

            <div className="mt-6 space-y-6">
              <div>
                <p className="text-sm font-semibold text-slate-800">Genel memnuniyet *</p>
                <div className="mt-2"><Stars value={rating} onChange={setRating} testid="survey-rating" /></div>
              </div>

              {questions.map((q) => (
                <div key={q.id} data-testid={`survey-q-${q.id}`}>
                  <p className="text-sm font-semibold text-slate-800">{q.text}</p>
                  {q.type === "rating" ? (
                    <div className="mt-2">
                      <Stars value={(answers[q.id] || {}).rating || 0} onChange={(v) => setAns(q.id, { rating: v })} testid={`survey-q-${q.id}-stars`} />
                    </div>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(q.options || []).map((opt) => {
                        const sel = (answers[q.id] || {}).choice === opt;
                        return (
                          <button
                            key={opt}
                            type="button"
                            data-testid={`survey-q-${q.id}-opt-${opt}`}
                            onClick={() => setAns(q.id, { choice: opt })}
                            className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${sel ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 bg-white text-slate-600 hover:border-emerald-400"}`}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}

              <div>
                <p className="text-sm font-semibold text-slate-800">Görüşleriniz</p>
                <textarea
                  data-testid="survey-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="Beğendikleriniz, önerileriniz veya şikayetleriniz..."
                  className="mt-2 w-full rounded-xl border border-slate-300 p-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium text-slate-600">Ad Soyad *</p>
                  <input
                    data-testid="survey-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Adınız Soyadınız"
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-600">Telefon *</p>
                  <input
                    data-testid="survey-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    inputMode="tel"
                    placeholder="05xx xxx xx xx"
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-400">Ad ve telefonunuz yalnızca geri bildiriminizin doğruluğunu teyit etmek için alınır.</p>

              {err && <p className="text-sm font-medium text-red-600" data-testid="survey-error">{err}</p>}

              <button
                data-testid="survey-submit"
                onClick={submit}
                disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3.5 font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                Değerlendirmeyi Gönder
              </button>
            </div>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-slate-400">{COMPANY.web}</p>
      </div>
    </div>
  );
}
